import axios from "axios";
import { proxyUrl } from "../config";
import { ElementsPromotionType } from "./ElementsPromotionsService";
import { setupInterceptorsTo } from "./Interceptors";
import { ContractDocsVersionType } from "./ContractDocsService";
import { UserIdentityType } from "./UserIdentitiesService";
import { ISignature } from "./SignaturesService";
setupInterceptorsTo(axios);

interface IElementPrice {
  date: Date;
  dateString: string;
  events: number;
  price: number;
}
export type ElementPrice = IElementPrice;

interface IElementAmount {
  elementAir: number;
  elementEarth: number;
  elementFire: number;
  elementWater: number;
  elementIA: number;
  elementTransactions: IElementTransaction[];
  elementsSigned: ElementSignedType[];
}
export type ElementAmountType = IElementAmount;

interface IElementTransaction {
  elementAmountId: number;
  elementAmount: ElementAmountType;
  amountType: string;
  amountElement: number;
  concept: string;
  description: string;
  transactionDate: Date;
  elementPrmotionId?: number;
  elementPromotion?: ElementsPromotionType;
}
export type ElementTransactionType = IElementTransaction;

interface IElementIA {
  elementsWaterUsed: number;
  elementsIA: number;
  reference: string;
  subject: string;
  price: number;
  areElementsApplied: boolean;
  savingsPlanId: number;
}
export type ElementIAObject = IElementIA;

interface IElementIAResponse {
  areElementsApplied: boolean;
  isPriceEqualZero: boolean;
  hasError: boolean;
  error: string;
}
export type ElementIAResponse = IElementIAResponse;

export type UpdateElementsAmountType = {
  savingsPlanId: number;
  air?: number;
  earth?: number;
  fire?: number;
  water?: number;
  iA?: number;
  concept: string;
  description: string;
};

export type ServiceType = {
  id?: number;
  name?: string;
  serviceType: string;
  elementIA?: number;
  elementWater?: number;
  elementEarth?: number;
  elementFire?: number;
  elementAir?: number;
};

export interface SavingsPlanElementsStatus {
  isSubscribed: boolean;
  isOutdatedContract: boolean;
  lastContractVersion?: string;
  sync: boolean;
}

export interface ElementContract {
  name: string;
  file: string;
  version: string;
  date: string;
  userIdentityFullname: string;
  userIdentityDocument: string;
}

export interface ElementSignedType {
  id: number;
  elementAmountId: number;
  userIdentityId: number;
  signatureId: number;
  signedContractURL: number;
  signedPromotionId?: number;
  contractDocsVersionsId: number;
  contractDocsVersions: ContractDocsVersionType[];
  elementAmount: ElementAmountType;
  userIdentity: UserIdentityType;
  signature: ISignature;
  signedPromotion: ElementsPromotionType;
}

export interface SignatureContractElements {
  userIdentityId: number;
  phone: string;
  expiresAt: Date;
  id: number;
  signAt: string;
  status: number;
}

export interface AllElementsAmountFromUser {
  savingPlanId: number;
  planName: string;
  water: number;
  ia: number;
  fire: number;
  earth: number;
  air: number;
}

export interface ElementContractInfo {
  elementAmountId: number;
  elementsIA: number;
  savingsPlanId: number;
  savingsPlanName: string;
  hasLatestVersionSigned: boolean;
}

export interface ElementIARefundItem {
  savingsPlanId: number;
  amount: number;
  refundCode: string;
  bankAccounts: string[];
}

export interface ElementIARefundValidationRequest {
  refundItems: ElementIARefundItem[];
}

export interface ElementIARefundValidationResponse {
  savingsPlanId: number;
  savingsPlanName: string;
  isValid: boolean;
  errorMessages: string[];
  availableElementsIA: number;
}

export type ElementContractResponse = string;

class ElementsService {
  public isSavingsPlanSubscribedToElements = async (
    savingsPlanId: number
  ): Promise<SavingsPlanElementsStatus> => {
    let url: string = `${proxyUrl}/IsSavingsPlanSubscribedToElements/${savingsPlanId}`;
    const req = await axios.get(url);
    return req.data;
  };
  public getElementContract = async (): Promise<ElementContractResponse> => {
    let url: string = `${proxyUrl}/GetElementContract`;
    const req = await axios.get(url);
    return req.data;
  };
  public getElementContractByName = async (
    elementContractName: string
  ): Promise<any> => {
    let url: string = `${proxyUrl}/getElementContractByName/${elementContractName}`;
    const req = await axios.get(url);
    return req.data;
  };
  public getAllPricesData = async (): Promise<ElementPrice[]> => {
    let url: string = `${proxyUrl}/GetAllPricesData`;
    const req = await axios.get(url);
    return req.data;
  };
  public generateElementPromotionPdfs = async (
    savingsPlanId: number,
    elementPromotionSlug: string
  ): Promise<boolean> => {
    let url: string = `${proxyUrl}/generateElementPromotionPdfs/${savingsPlanId}/${elementPromotionSlug}`;
    const req = await axios.post(url);
    return req.data;
  };
  public getElementsAmountBySavingsPlanId = async (
    savingsPlanId: number
  ): Promise<any> => {
    let url: string = `${proxyUrl}/elementsAmount/getBySavingsPlanId/${savingsPlanId}`;
    const req = await axios.get(url);
    return req.data;
  };
  public getElementsAmountBySavingsPlanIdCompetition = async (
    savingsPlanId: number
  ): Promise<any> => {
    let url: string = `${proxyUrl}/elementsAmount/getBySavingsPlanId/${savingsPlanId}?fromCompetition=true`;
    const req = await axios.get(url);
    return req.data;
  };
  public getOnlyElementsAmountByUsersSubscriptionId = async (
    usersSubscriptionId: number
  ): Promise<any> => {
    let url: string = `${proxyUrl}/OnlyElementsAmountByUsersSubscriptionId/${usersSubscriptionId}`;
    const req = await axios.get(url);
    return req.data;
  };
  public getAllContracts = async (
    savingsPlanId: number
  ): Promise<ElementContract[]> => {
    let url: string = `${proxyUrl}/GetAllContracts/${savingsPlanId}`;
    const req = await axios.get(url);
    return req.data;
  };
  public getElementContractPDF = async (id: string): Promise<any> => {
    let url: string = `${proxyUrl}/GetElementContractPDF/${id}`;
    const req = await axios.get(url);
    return req.data;
  };
  public getLastElementsPrice = async (): Promise<any> => {
    let url: string = `${proxyUrl}/elements/getLastElementsPrice`;
    const req = await axios.get(url);
    return req.data;
  };
  public elementsIA = async (
    toSend: ElementIAObject
  ): Promise<ElementIAResponse> => {
    let url: string = `${proxyUrl}/ElementsIA`;
    const req = await axios.post(url, toSend, {
      headers: { "content-type": "application/json" },
    });
    return req.data;
  };
  public updateElementsAmount = async (
    elements: UpdateElementsAmountType
  ): Promise<boolean> => {
    let url: string = `${proxyUrl}/elementsAmount/UpdateElementsAmount`;
    const req = await axios.patch(url, elements);
    return req.data;
  };
  public servicePrice = async (
    servicePriceTypeId: number
  ): Promise<ServiceType> => {
    let url: string = `${proxyUrl}/elements/services-prices/${servicePriceTypeId}`;
    const req = await axios.get(url);
    return req.data;
  };
  public transferElementsIA = async (data: any): Promise<any> => {
    let url: string = `${proxyUrl}/ElementsIATransferRefund`;
    const req = await axios.post(url, data, {
      headers: { "content-type": "application/json" },
    });
    return req.data;
  };
  public createElementAmount = async (
    savingsPlanId: any
  ): Promise<SignatureContractElements[]> => {
    let url: string = `${proxyUrl}/ElementsContractInfrastructure/${savingsPlanId}`;
    const req = await axios.post(url);
    return req.data;
  };
  public createSampleProfileElementAmount = async (
    savingsPlanId: number,
    sampleProfileId: number
  ): Promise<any> => {
    const url: string = `${proxyUrl}/elementAmounts/sampleProfile/${savingsPlanId}/${sampleProfileId}`;
    const req = await axios.post(url);
    return req.data;
  };
  public getAllElementsAmountFromUser = async (
    userId: number
  ): Promise<AllElementsAmountFromUser[]> => {
    let url: string = `${proxyUrl}/AllElementsAmountFromUser/${userId}`;
    const req = await axios.get(url);
    return req.data;
  };
  public checkElementsContractByAllSavingPlans = async (): Promise<
    ElementContractInfo[]
  > => {
    let url: string = `${proxyUrl}/CheckElementsContractByAllSavingPlans`;
    const req = await axios.get(url);
    return req.data;
  };
  public validateElementIARefunds = async (
    request: ElementIARefundValidationRequest
  ): Promise<ElementIARefundValidationResponse[]> => {
    let url: string = `${proxyUrl}/ValidateElementIARefunds`;
    const req = await axios.post(url, request);
    return req.data;
  };
}

const ElementService = new ElementsService();
export default ElementService;
